import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityId,
} from '@reduxjs/toolkit';
import { postJSON } from 'api/fetch';
import { getEntitiesFromMap } from 'fe-core/Resources/helpers';
import { RootState } from 'initializers/types';
import createCachedSelector from 're-reselect';
import Shift from 'resources/Shift';

import RoleLaborTotals from 'features/scheduleBuilder/ScheduleBuilderView/resources/RoleLaborTotals';
import Schedule from 'features/scheduleBuilder/ScheduleBuilderView/resources/Schedule';

import {
  captureHydrationException,
  getSliceState,
  selectAttributeForEntity,
} from '../helpers';
import {
  ScheduleFetchAction,
  ShiftFetchAction,
  ShiftUpdateAction,
} from '../types';

import { SLICE_NAME } from './constants';
import {
  Role,
  RoleOption,
  RoleSerializedEntity,
  RoleShiftProps,
  RolesState,
} from './types';

const rolesAdapter = createEntityAdapter<Role>({
  sortComparer: (a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
});

const initialState: RolesState = {
  ...rolesAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

export const createRole = createAsyncThunk(
  `${SLICE_NAME}/createRole`,
  async (payload: { name: string }) => {
    const result = await postJSON('/schedule_builder/roles', { role: payload });

    return result;
  }
);

export const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setAll: rolesAdapter.setAll,
    removeAll: rolesAdapter.removeAll,
  },
  extraReducers: builder => {
    builder.addCase('resources/fetch/pending', (state, action: any) => {
      if (action.meta?.arg?.name === 'schedule') {
        state.status = 'pending';
      }
    });
    builder.addCase(
      Shift.resourceActionEvent('update'),
      (state, action: ShiftUpdateAction) => {
        try {
          state.status = 'succeeded';
          if (action.payload?.included) {
            try {
              const roles: Role[] = (
                action.payload.included as RoleSerializedEntity[]
              )
                .filter(
                  (entity: RoleSerializedEntity) => entity.type === 'role'
                )
                .map((entity: RoleSerializedEntity) => {
                  let departmentRelationship: any | undefined;

                  if (entity?.relationships) {
                    const flattenedRelationships = Object.fromEntries(
                      Object.entries(entity.relationships || {}).map(
                        ([k, v]: [string, any]) => [k, v.data]
                      )
                    );
                    departmentRelationship = flattenedRelationships?.department;
                  }

                  return {
                    id: entity.id,
                    type: entity.type,
                    name: entity.attributes.name,
                    color: entity.attributes.color,
                    archivedAt: entity.attributes.archivedAt,
                    payrollId: entity.attributes.payrollId,
                    department: departmentRelationship,
                  };
                });
              rolesAdapter.upsertMany(state, roles);
            } catch (error) {
              captureHydrationException(state, error, SLICE_NAME);
            }
          }
        } catch (error) {
          captureHydrationException(state, error, SLICE_NAME);
        }
      }
    );
    builder.addCase(
      Shift.resourceActionEvent('fetch'),
      (state, action: ShiftFetchAction) => {
        try {
          state.status = 'succeeded';
          if (action.payload?.included) {
            try {
              const roles: Role[] = (
                action.payload.included as RoleSerializedEntity[]
              )
                .filter(
                  (entity: RoleSerializedEntity) => entity.type === 'role'
                )
                .map((entity: RoleSerializedEntity) => {
                  let departmentRelationship: any | undefined;

                  if (entity?.relationships) {
                    const flattenedRelationships = Object.fromEntries(
                      Object.entries(entity.relationships || {}).map(
                        ([k, v]: [string, any]) => [k, v.data]
                      )
                    );
                    departmentRelationship = flattenedRelationships?.department;
                  }

                  return {
                    id: entity.id,
                    type: entity.type,
                    name: entity.attributes.name,
                    color: entity.attributes.color,
                    archivedAt: entity.attributes.archivedAt,
                    payrollId: entity.attributes.payrollId,
                    department: departmentRelationship,
                  };
                });
              rolesAdapter.upsertMany(state, roles);
            } catch (error) {
              captureHydrationException(state, error, SLICE_NAME);
            }
          }
        } catch (error) {
          captureHydrationException(state, error, SLICE_NAME);
        }
      }
    );
    builder.addCase(
      Schedule.resourceActionEvent('fetch'),
      (state, action: ScheduleFetchAction) => {
        try {
          state.status = 'succeeded';
          if (action.payload?.included) {
            try {
              const roles: Role[] = (
                action.payload.included as RoleSerializedEntity[]
              )
                .filter(
                  (entity: RoleSerializedEntity) => entity.type === 'role'
                )
                .map((entity: RoleSerializedEntity) => {
                  let departmentRelationship: any | undefined;

                  if (entity?.relationships) {
                    const flattenedRelationships = Object.fromEntries(
                      Object.entries(entity.relationships || {}).map(
                        ([k, v]: [string, any]) => [k, v.data]
                      )
                    );
                    departmentRelationship = flattenedRelationships?.department;
                  }

                  return {
                    id: entity.id,
                    type: entity.type,
                    name: entity.attributes.name,
                    color: entity.attributes.color,
                    archivedAt: entity.attributes.archivedAt,
                    payrollId: entity.attributes.payrollId,
                    department: departmentRelationship,
                  };
                });

              rolesAdapter.setAll(state, roles);
            } catch (error) {
              captureHydrationException(state, error, SLICE_NAME);
            }
          }
        } catch (error) {
          captureHydrationException(state, error, SLICE_NAME);
        }
      }
    );
  },
});

export const {
  selectAll: selectAllRoles,
  selectById: selectRoleById,
  selectIds: selectRoleIds,
  selectEntities: selectRoleEntities,
} = rolesAdapter.getSelectors<RootState>(
  state => getSliceState(state)?.[SLICE_NAME] || initialState
);

// eslint-disable-next-line
//@ts-ignore
export const selectRolesByDepartment = createSelector(
  [selectAllRoles, (_: RootState, departmentId: string) => departmentId],
  (roles, departmentId): Role[] =>
    roles.filter(role => role.department?.id === departmentId)
);

// eslint-disable-next-line
//@ts-ignore
export const selectActiveRoles = createSelector(
  [selectAllRoles],
  (roles): Role[] => roles.filter(role => !role.archivedAt)
);

export const selectRoleIdNameMap = createSelector(
  [selectAllRoles],
  (roles): { [key: number]: string } =>
    roles.reduce((acc, role) => {
      acc[role.id as number] = role.name;
      return acc;
    }, {} as { [key: number]: string })
);

export const selectTeamRoles = createSelector(
  [selectActiveRoles],
  (roles): RoleOption[] =>
    roles.map(role => ({
      value: role.name,
      label: role.name,
      color: role.color,
    }))
);

export const selectRoleLaborTotalsByRoleId = createCachedSelector(
  (state: RootState, { roleId }: { roleId: EntityId }) => roleId,
  RoleLaborTotals.selectAllEntities,
  RoleLaborTotals.selectEntityMap,
  (roleId, roleLaborTotals, roleLaborTotalsMap) => {
    if (roleId === 'uncategorized') {
      return roleLaborTotals.filter(
        laborTotal => !laborTotal.relationships.role.data
      );
    }

    const ids: EntityId[] = [];

    for (const laborTotal of roleLaborTotals) {
      if (laborTotal.relationships?.role?.data?.id === roleId) {
        ids.push(laborTotal.id);
      }
    }
    return getEntitiesFromMap(ids, roleLaborTotalsMap) || [];
  }
)((_, { roleId }) => `selectLaborTotalsByRoleId-${roleId}`);

export const selectShiftRoleOption = createCachedSelector(
  (
    state: RootState,
    { roleId: id, shiftId }: RoleShiftProps
  ): RoleOption | null => {
    const roleId =
      id || Shift.selectRelationshipId(state, 'role', String(shiftId));

    if (!roleId) return null;

    const role = selectRoleById(state, roleId);
    if (!role) return null;

    return {
      value: role.name,
      label: role.name,
      color: role.color,
    };
  },
  (option: any) => option
)(
  (_: any, { shiftId, roleId }: RoleShiftProps) =>
    `selectShiftRoleOption-${shiftId}-${roleId}`
);

// eslint-disable-next-line
//@ts-ignore
export const selectRoleByName = createSelector(
  [selectAllRoles, (_: RootState, name: string) => name],
  (roles, name) => roles.find(role => role.name === name)
);

export function selectRoleWageAttribute<K extends keyof Role>(opts: {
  state: RootState;
  id: EntityId;
  attribute: K;
}): Role[K] | undefined {
  return selectAttributeForEntity({
    state: opts.state,
    id: opts.id,
    attribute: opts.attribute,
    entitySelector: selectRoleById,
  });
}

export const selectRoleIdRoleNameMap = createSelector(selectAllRoles, roles =>
  roles?.reduce((memo, role) => {
    if (role.name) {
      memo[Number(role.id)] = role.name;
    }
    return memo;
  }, {} as { [key: number]: string })
);

export const { setAll, removeAll } = slice.actions;
export const { reducer } = slice;
